import {
  chain,
  Either,
  isRight,
  left,
  map,
  mapLeft,
  Right,
  right
} from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import { chain as chainTE, fromEither, TaskEither, tryCatch } from "fp-ts/lib/TaskEither";
import * as t from "io-ts";
import { Type, TypeOf, union } from "io-ts";
import { PathReporter } from "io-ts/lib/PathReporter";
import "whatwg-fetch";

const ApiServerErrorsRT = t.type({
  "server-errors": t.array(t.string)
});

export type ApiError = string[];

const checkServerErrors = <T>(e: T | TypeOf<typeof ApiServerErrorsRT>): Either<string[], T> => {
  const errs = ApiServerErrorsRT.decode(e);
  if (isRight(errs)) {
    const r = errs.right["server-errors"];
    return left([`Server responded with errors`, ...r]);
  } else {
    return right(e) as Right<T>;
  }
};

const parseJson = (response: Response) =>
  tryCatch<string[], string>(
    async () => await response.json(),
    err => [`Could not parse response as JSON`, `${err}`]
  );


const mkUrl = (prefix: string, suffix: string, qsobj?: any): string => {
  const location = prefix + (prefix.slice(-1) === `/` ? "" : `/`) + suffix;
  if (qsobj) {
    const qs = new URLSearchParams();
    qs.append(`input`, JSON.stringify(qsobj));
    return location + `?` + qs.toString();
  } else {
    return location;
  }
};

const httpGet = (url: string) =>
  tryCatch<string[], Response>(
    async () => {
      const resp = await fetch(url)
      if (!resp.ok) {
        throw new Error(`Network response was ${resp.status}`);
      }
      return resp
    },
    err => [`${err}`]
  );

const httpPost = (url: string, body: any) =>
  tryCatch<string[], Response>(
    async () => {
      const resp = await fetch(url, { method: "POST", body: JSON.stringify(body) })
      if (!resp.ok) {
        throw new Error(`Network response was ${resp.status}`);
      }
      return resp
    },
    err => [`${err}`]
  );

const validatingCall = <I, O>(httpCall: TaskEither<string[], Response>, codec: Type<I>, domainconverter: (so: TypeOf<typeof codec>) => O): Promise<Either<ApiError, O>> => {

  const codecWithServerErrors = union([codec, ApiServerErrorsRT])
  const decoder = (e: string) => codecWithServerErrors.decode(e)
  const errorChecker =
    (e: TypeOf<typeof codec> | TypeOf<typeof ApiServerErrorsRT>): Either<string[], TypeOf<typeof codec>> => {
      return checkServerErrors<TypeOf<typeof codec>>(e)
    }
  const domainConverter = (e: TypeOf<typeof codec>): O => domainconverter(e)

  async function call(): Promise<Either<ApiError, O>> {
    return await pipe(
      httpCall,
      chainTE(e => parseJson(e)),
      chainTE(e =>
        pipe(
          decoder(e),
          mapLeft(e => PathReporter.report(left(e))),
          chain(e => errorChecker(e)),
          map(e => domainConverter(e)),
          fromEither
        )
      )
    )();
  }

  return call()
}

const get = <I, O>(url: string, codec: Type<I>, domainconverter: (so: TypeOf<typeof codec>) => O): Promise<Either<ApiError, O>> => {
  return validatingCall(httpGet(url), codec, domainconverter)
}

const post = <I, O>(url: string, body: any, codec: Type<I>, domainconverter: (so: TypeOf<typeof codec>) => O): Promise<Either<ApiError, O>> => {
  return validatingCall(httpPost(url, body), codec, domainconverter)
}

export { get, post, mkUrl }
